import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [snapViewport] - Viewport wrapper for snap scroller.
 * @property {HTMLElement} [snapScroller] - UL element that scroll-snaps between slides.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Einmal cachen
    this.#viewport = this.refs.snapViewport || this.querySelector('[ref="snapViewport"]');
    this.#isLVPattern = this.#viewport?.classList.contains('lv-product-page-header__media') ?? false;

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Snap-scroller (UL/LI) based gallery
    const snapScroller = this.refs.snapScroller;
    if (snapScroller instanceof HTMLElement) {
      snapScroller.addEventListener('scroll', this.#handleSnapScroll, { signal, passive: true });
      
      // LV-Pattern: Freeze bei Interaction-Start (verhindert Layout-Konflikte)
      if (this.#isLVPattern) {
        snapScroller.addEventListener('pointerdown', this.#freezeHeight.bind(this), { signal, passive: true });
        snapScroller.addEventListener('touchstart', this.#freezeHeight.bind(this), { signal, passive: true });
        snapScroller.addEventListener('wheel', this.#freezeHeight.bind(this), { signal, passive: true });
        
        // scrollend Event (wenn supported) -> sofort settle ohne debounce
        if ('onscrollend' in window) {
          snapScroller.addEventListener('scrollend', this.#handleScrollEnd.bind(this), { signal, passive: true });
        }
      }
    }

    window.addEventListener('resize', this.#handleResize, { signal });
    this.#viewportMediaQuery.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Set initial height once layout is settled
    requestAnimationFrame(() => {
      this.#setupSnapObserver();
      this.#updateAdaptiveHeight();
      
      // LV-Pattern: Initial Viewport-Width cachen
      if (this.#isLVPattern) {
        this.#getViewportWidth();
      }
    });
  }

  #controller = new AbortController();
  #viewportMediaQuery = window.matchMedia('(max-width: 749px)');
  /** @type {IntersectionObserver | null} */
  #snapObserver = null;
  /** @type {number} */
  #currentIndex = 0;
  /** @type {number | null} */
  #scrollEndTimeout = null;
  /** @type {number | null} */
  #scrollRaf = null;
  /** @type {number} */
  #lastRatio = 1;
  /** @type {number | null} */
  #programmaticScrollTimeout = null;
  /** @type {boolean} */
  #isProgrammaticScrolling = false;
  /** @type {boolean} */
  #ratioFrozen = false;
  /** @type {number} */
  #lastKnownWidth = 0;
  /** @type {HTMLElement | null} */
  #viewport = null;
  /** @type {boolean} */
  #isLVPattern = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();

    // Cleanup: Timeouts und RAF aufräumen
    if (this.#scrollEndTimeout) window.clearTimeout(this.#scrollEndTimeout);
    if (this.#scrollRaf) window.cancelAnimationFrame(this.#scrollRaf);

    // Observer disconnecten (verhindert Memory Leaks)
    this.#snapObserver?.disconnect();
    this.#snapObserver = null;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.select(event.detail.index, undefined, { animate: false });
  };

  #handleResize = () => {
    this.#updateAdaptiveHeight();
    this.#setupSnapObserver();
  };

  #handleMediaQueryChange = () => {
    this.#updateAdaptiveHeight();
    this.#setupSnapObserver();
  };

  /**
   * Freeze viewport height während User interagiert (verhindert Layout-Konflikte mit Scroll-Snap)
   */
  #freezeHeight() {
    if (this.#ratioFrozen) return;
    
    if (!this.#isLVPattern || !this.#viewport) return;

    this.#ratioFrozen = true;

    // Aktuelle Höhe -> padding-top Prozent (einmalig lesen)
    const rect = this.#viewport.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    
    if (w > 0 && h > 0) {
      const frozenPaddingTop = `${(h / w) * 100}%`;
      this.#viewport.style.setProperty('--frozen-padding-top', frozenPaddingTop);
    }
    
    this.#viewport.setAttribute('data-ratio-frozen', '');
    this.#viewport.removeAttribute('data-ratio-animating');
  }

  /**
   * Unfreeze und animiere zur Ziel-Ratio (nach Scroll-Settle)
   * @param {number} ratio
   */
  #unfreezeAndAnimateTo(ratio) {
    if (!this.#isLVPattern || !this.#viewport) return;

    // FIX: Konsistent mit CSS - nutze nur --active-media-ratio + padding-top Transition
    // Kein transform scaleY, da CSS ::before padding-top animiert
    this.#viewport.removeAttribute('data-ratio-frozen');
    this.#viewport.style.removeProperty('--frozen-padding-top');

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReducedMotion) {
      // CSS animiert padding-top im ::before via data-ratio-animating
      this.#viewport.setAttribute('data-ratio-animating', '');
      this.#viewport.style.setProperty('--active-media-ratio', String(ratio));

      // Cleanup nach Animation (CSS transition auf padding-top)
      const cleanup = () => {
        if (!this.#viewport) return;
        this.#viewport.removeAttribute('data-ratio-animating');
        this.#ratioFrozen = false;
      };

      // Fallback: setTimeout falls transitionend nicht ausgelöst wird (Pseudo-Elemente sind unzuverlässig)
      window.setTimeout(() => {
        if (this.#viewport?.hasAttribute('data-ratio-animating')) {
          cleanup();
        }
      }, 320); // 300ms CSS transition + 20ms Puffer
    } else {
      // Reduced motion: sofort setzen
      this.#viewport.style.setProperty('--active-media-ratio', String(ratio));
      this.#ratioFrozen = false;
    }
  }

  /**
   * Bestimmt Viewport-Breite (einmalig lesen, cachen für Hot-Path)
   */
  #getViewportWidth() {
    if (!this.#viewport) return 0;

    const w = this.#viewport.clientWidth || this.#viewport.getBoundingClientRect().width;
    this.#lastKnownWidth = w;
    return w;
  }

  /**
   * Bestimmt aktiven Index über scrollLeft / width (stabiler als aria-hidden)
   */
  #getActiveIndex() {
    const scroller = this.refs.snapScroller;
    if (!scroller) return 0;

    const w = this.#lastKnownWidth || scroller.clientWidth;
    if (!w) return 0;

    // scrollLeft / slideWidth ist stabil, kein offsetLeft pro Slide
    return Math.round(scroller.scrollLeft / w);
  }

  /**
   * Berechnet Ratio für Index mit Clamping
   * @param {number} index
   * @returns {number}
   */
  #ratioForIndex(index) {
    const slides = this.snapSlides;
    const slide = slides[index];
    if (!(slide instanceof HTMLElement)) return 1;

    const ratioAttr = slide.getAttribute('data-media-aspect-ratio');
    let ratio = ratioAttr ? Number.parseFloat(ratioAttr) : NaN;

    if (!Number.isFinite(ratio) || ratio <= 0) {
      ratio = this.#isLVPattern ? 0.8 : 1;
    }

    // Hochformat begrenzen: Minimum Ratio von 0.85
    if (ratio < 1) {
      ratio = Math.max(0.85, ratio);
    }

    return Math.max(0.1, Math.min(10, ratio));
  }

  /**
   * @param {HTMLElement} [slide]
   */
  #updateAdaptiveHeight(slide = undefined) {
    // Für adaptive-height: LV-Pattern hat immer adaptive-height, Legacy nur wenn Klasse gesetzt
    if (!this.#isLVPattern && !this.classList.contains('media-gallery--adaptive-height')) return;

    const scroller = this.refs.snapScroller || this.querySelector('[ref="snapScroller"]');
    if (!this.#viewport || !(scroller instanceof HTMLElement)) return;

    // Während Freeze: keine Ratio-Update
    if (this.#ratioFrozen) return;

    const activeSlide =
      slide ||
      (this.snapSlides?.[this.#currentIndex] instanceof HTMLElement ? this.snapSlides[this.#currentIndex] : null) ||
      (scroller.querySelector('[data-index]') instanceof HTMLElement ? scroller.querySelector('[data-index]') : null);

    if (!(activeSlide instanceof HTMLElement)) return;

    // READ PHASE: Sammle alle Layout-Reads einmalig
    const ratioAttr = activeSlide.getAttribute('data-media-aspect-ratio');
    let ratio = ratioAttr ? Number.parseFloat(ratioAttr) : NaN;

    if (!Number.isFinite(ratio) || ratio <= 0) {
      // Nur einmal lesen, nicht während Animation
      const cssRatio = activeSlide.style.getPropertyValue('--media-preview-ratio').trim();
      if (!cssRatio) {
        // Fallback: getComputedStyle nur wenn nötig, aber außerhalb von rAF-Loop
        const computed = getComputedStyle(activeSlide);
        const cssRatioComputed = computed.getPropertyValue('--media-preview-ratio').trim();
        ratio = cssRatioComputed ? Number.parseFloat(cssRatioComputed) : NaN;
      } else {
        ratio = Number.parseFloat(cssRatio);
      }
    }

    if (!Number.isFinite(ratio) || ratio <= 0) {
      // LV-Pattern Default: 0.8 (portrait)
      ratio = this.#isLVPattern ? 0.8 : 1;
    }
    
    // Hochformat begrenzen: Bilder sollen nicht zu lang werden
    // Minimum Ratio von 0.85 für leichtes Hochformat statt extremem Hochformat
    if (ratio < 1) {
      ratio = Math.max(0.85, ratio);
    }
    
    ratio = Math.max(0.1, Math.min(10, ratio));

    // Skip if unchanged (prevents needless transitions und Micro-Jitter)
    if (Math.abs(ratio - this.#lastRatio) < 0.0001) return;
    this.#lastRatio = ratio;

    // WRITE PHASE: Nur Writes in rAF
    requestAnimationFrame(() => {
      if (!this.#viewport) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const shouldAnimate = !prefersReducedMotion;

      // FIX: CSS animiert padding-top im ::before, nicht transform
      // will-change wird im CSS über data-ratio-animating gesteuert, nicht hier
      if (shouldAnimate) {
        this.#viewport.setAttribute('data-ratio-animating', '');
      }

      // LV-Style: Nur Viewport-Variable setzen (nicht auf allen Slides)
      // Slides haben jetzt height: 100% und füllen den Viewport immer
      // LV-Pattern verwendet --active-media-ratio für padding-top calc() im ::before Element
      this.#viewport.style.setProperty('--active-media-ratio', String(ratio));

      if (shouldAnimate) {
        // transitionend: Filter auf padding-top (wird vom ::before Element ausgelöst)
        // Fallback: setTimeout falls transitionend nicht ausgelöst wird
        const cleanup = () => {
          if (!this.#viewport) return;
          this.#viewport.removeAttribute('data-ratio-animating');
        };
        
        // Versuche transitionend (kann bei Pseudo-Elementen unzuverlässig sein)
        /**
         * @param {TransitionEvent} e
         */
        const handleTransitionEnd = (e) => {
          // padding-top Transition vom ::before
          if (e.propertyName === 'padding-top' || !e.propertyName) {
            cleanup();
          }
        };
        
        this.#viewport.addEventListener('transitionend', handleTransitionEnd, { once: true });
        
        // Fallback: setTimeout als Backup (falls transitionend nicht ausgelöst wird)
        const transitionDuration = 300; // ms - muss mit CSS übereinstimmen
        setTimeout(() => {
          if (this.#viewport?.hasAttribute('data-ratio-animating')) {
            cleanup();
          }
        }, transitionDuration + 50); // +50ms Puffer
      }
    });
  }

  /**
   * Handle scrollend Event (wenn supported) -> sofort settle ohne debounce
   */
  #handleScrollEnd = () => {
    if (this.#scrollEndTimeout) window.clearTimeout(this.#scrollEndTimeout);
    this.#scrollEndTimeout = null;
    
    this.removeAttribute('snap-scrolling');
    
    if (this.#isLVPattern) {
      // LV-Pattern: Aktiviere Knob-Transition nach Scrollen
      if (this.#viewport) {
        const knob = this.#viewport.querySelector('.lv-mini-slider__knob');
        if (knob instanceof HTMLElement) {
          knob.style.transition = '';
          // will-change bleibt gesetzt für GPU-Beschleunigung
        }
      }
      
      // Settle: bestimme Index über scrollLeft / width (stabiler)
      this.#getViewportWidth(); // Cache width für settle
      const index = this.#getActiveIndex();
      const ratio = this.#ratioForIndex(index);
      
      // Update currentIndex für Controls-Sync
      if (index !== this.#currentIndex) {
        this.#currentIndex = index;
        this.#syncControls();
      }
      
      // Unfreeze und animiere zur Ziel-Ratio
      this.#unfreezeAndAnimateTo(ratio);
    } else {
      // Legacy: bestehende Logik
      if (this.#viewport) {
        const knob = this.#viewport.querySelector('.lv-mini-slider__knob');
        if (knob instanceof HTMLElement) {
          knob.style.transition = '';
        }
      }
      this.#syncFromScrollPosition();
    }
  };

  #handleSnapScroll = () => {
    // While the user is actively swiping, avoid changing viewport height (Safari scroll-snap is sensitive to layout changes mid-scroll)
    if (!this.hasAttribute('snap-scrolling')) {
      this.setAttribute('snap-scrolling', '');
      
      // LV-Pattern: Freeze height während Scrollen
      this.#freezeHeight();
      
      // LV-Pattern: Deaktiviere Knob-Transition während Scrollen
      if (this.#viewport) {
        const knob = this.#viewport.querySelector('.lv-mini-slider__knob');
        if (knob instanceof HTMLElement) {
          knob.style.transition = 'none';
          // will-change während Scrollen beibehalten für Performance
          if (!knob.hasAttribute('data-knob-animating')) {
            knob.style.willChange = 'transform';
          }
        }
      }
    }

    // Keep the progress indicator responsive while scrolling
    if (!this.#scrollRaf) {
      this.#scrollRaf = window.requestAnimationFrame(() => {
        this.#scrollRaf = null;
        this.#syncProgressFromScroll();
      });
    }

    if (this.#scrollEndTimeout) window.clearTimeout(this.#scrollEndTimeout);

    // Debounced settle: nach Scroll-Ende Ratio updaten (nur wenn scrollend nicht supported)
    this.#scrollEndTimeout = window.setTimeout(() => {
      this.#handleScrollEnd();
    }, 110); // 110ms debounce für settle

    this.#setupSnapObserver();
  };

  #syncProgressFromScroll() {
    const scroller = this.refs.snapScroller;
    const slides = this.snapSlides;
    if (!(scroller instanceof HTMLElement) || !slides.length) return;

    // READ PHASE: Sammle alle Layout-Reads einmalig
    const slideWidth = scroller.clientWidth;
    if (!slideWidth) return;
    const scrollLeft = scroller.scrollLeft;

    // Berechne Progress
    const progress = Math.max(0, Math.min(slides.length - 1, scrollLeft / slideWidth));
    const currentIndex = Math.round(progress);

    // WRITE PHASE: Nur Writes in rAF
    requestAnimationFrame(() => {
      // LV-Pattern: Progress Track/Knob aktualisieren (GPU-beschleunigt)
      if (this.#viewport) {
        const knob = this.#viewport.querySelector('.lv-mini-slider__knob');
        if (knob instanceof HTMLElement) {
          const track = knob.parentElement;
          if (track instanceof HTMLElement) {
            const trackWidth = track.clientWidth || 60;
            const knobPixelWidth = trackWidth / slides.length;
            const knobTransform = currentIndex * knobPixelWidth;
            
            // GPU-beschleunigt: width einmalig setzen (nicht animiert), nur translateX animieren
            // will-change setzen wenn animiert wird
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (!prefersReducedMotion && !knob.hasAttribute('data-knob-animating')) {
              knob.style.willChange = 'transform';
              knob.setAttribute('data-knob-animating', '');
            }
            
            // Breite einmalig setzen (nicht animiert, kein Layout-Trigger)
            knob.style.width = `${knobPixelWidth}px`;
            // GPU-beschleunigte Animation: Nur translateX (transform)
            knob.style.transform = `translateX(${knobTransform}px)`;
          }
        }
      }

      // Legacy: slideshow-controls
      this.querySelectorAll('slideshow-controls').forEach((controls) => {
        if (!(controls instanceof HTMLElement)) return;
        controls.style.setProperty('--slideshow-progress-count', String(slides.length));
        controls.style.setProperty('--slideshow-progress-index', String(progress));
      });
    });
  }

  #syncFromScrollPosition() {
    const scroller = this.refs.snapScroller;
    const slides = this.snapSlides;
    if (!(scroller instanceof HTMLElement) || !slides.length) return;

    // READ PHASE: Sammle alle Layout-Reads einmalig
    const scrollLeft = scroller.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Infinity;

    // Sammle alle offsetLeft-Werte in einem Batch
    const slidePositions = [];
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      if (!(slide instanceof HTMLElement)) continue;
      slidePositions.push({
        index: i,
        offsetLeft: slide.offsetLeft,
      });
    }

    // Berechne closestIndex aus gesammelten Werten
    for (const { index, offsetLeft } of slidePositions) {
      const distance = Math.abs(offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    }

    // WRITE PHASE: Nur Writes, keine weiteren Reads
    if (closestIndex === this.#currentIndex) {
      this.#updateAdaptiveHeight(slides[this.#currentIndex]);
      return;
    }

    this.#currentIndex = closestIndex;
    this.#syncControls();
    this.#updateAdaptiveHeight(slides[this.#currentIndex]);
  }

  #setupSnapObserver() {
    const scroller = this.refs.snapScroller;
    const slides = this.snapSlides;
    if (!(scroller instanceof HTMLElement) || !slides.length) return;

    if (this.#snapObserver) return;

    this.#snapObserver = new IntersectionObserver(
      (entries) => {
        // Avoid changing the viewport ratio mid-swipe (Safari scroll-snap can become unstable with layout changes)
        if (this.hasAttribute('snap-scrolling')) return;

        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];

        const el = visible?.target;
        if (!(el instanceof HTMLElement)) return;

        const indexAttr = el.getAttribute('data-index');
        const index = indexAttr ? Number.parseInt(indexAttr, 10) : NaN;
        if (!Number.isFinite(index)) return;

        if (index === this.#currentIndex) return;
        this.#currentIndex = index;
        this.#syncControls();
        this.#updateAdaptiveHeight(el);
      },
      { root: scroller, threshold: [0.6] }
    );

    slides.forEach((slide) => this.#snapObserver?.observe(slide));
    this.#syncControls();
    this.#updateAdaptiveHeight(slides[this.#currentIndex]);
  }

  #syncControls() {
    const count = this.snapSlides.length || 1;
    const index = this.#currentIndex;

    // LV-Pattern: Progress Track/Knob aktualisieren (GPU-beschleunigt)
    if (this.#viewport) {
      const knob = this.#viewport.querySelector('.lv-mini-slider__knob');
      if (knob instanceof HTMLElement) {
        const track = knob.parentElement;
        if (track instanceof HTMLElement) {
          const trackWidth = track.clientWidth || 60;
          const knobPixelWidth = trackWidth / count;
          const knobTransform = index * knobPixelWidth;
          
          // GPU-beschleunigt: width einmalig setzen (nicht animiert), nur translateX animieren
          // will-change setzen wenn animiert wird
          const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (!prefersReducedMotion && !knob.hasAttribute('data-knob-animating')) {
            knob.style.willChange = 'transform';
            knob.setAttribute('data-knob-animating', '');
            
            // Cleanup nach Animation
            const cleanup = () => {
              if (knob.hasAttribute('data-knob-animating')) {
                knob.style.willChange = '';
                knob.removeAttribute('data-knob-animating');
              }
            };
            
            knob.addEventListener('transitionend', cleanup, { once: true });
            // Fallback: setTimeout falls transitionend nicht ausgelöst wird
            setTimeout(cleanup, 250);
          }
          
          // Breite einmalig setzen (nicht animiert, kein Layout-Trigger)
          knob.style.width = `${knobPixelWidth}px`;
          // GPU-beschleunigte Animation: Nur translateX (transform)
          knob.style.transform = `translateX(${knobTransform}px)`;
        }
      }

      // Update aria-hidden und is-active für Slides
      const slides = this.snapSlides;
      slides.forEach((slide, i) => {
        if (slide instanceof HTMLElement) {
          slide.setAttribute('aria-hidden', String(i !== index));
          slide.classList.toggle('is-active', i === index);
        }
      });
    }

    // Legacy: slideshow-controls
    this.querySelectorAll('slideshow-controls').forEach((controls) => {
      if (!(controls instanceof HTMLElement)) return;
      controls.style.setProperty('--slideshow-progress-count', String(count));
      controls.style.setProperty('--slideshow-progress-index', String(index));

      const current = controls.querySelector('[ref="current"]');
      if (current) current.textContent = String(index + 1);

      controls.querySelectorAll('[ref="dots[]"], [ref="thumbnails[]"]').forEach((el, i) => {
        if (!(el instanceof HTMLElement)) return;
        el.setAttribute('aria-selected', String(i === index));
      });
    });
  }

  /**
   * Scroll to a given index (used by on:click="/select/:index")
   * @param {number} index
   * @param {Event} [event]
   * @param {{ animate?: boolean }} [options]
   */
  select(index, event, options = {}) {
    event?.preventDefault?.();
    const scroller = this.refs.snapScroller;
    const slides = this.snapSlides;
    if (!(scroller instanceof HTMLElement) || !slides.length) return;

    const target = slides[index];
    if (!(target instanceof HTMLElement)) return;

    // READ PHASE: Layout-Read einmalig
    const targetOffsetLeft = target.offsetLeft;

    // WRITE PHASE: Scroll-Operation
    // Respektiere Reduced Motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldAnimate = !prefersReducedMotion && options.animate !== false;

    // LV-Pattern: Bei programmatic scroll smooth behavior explizit setzen (CSS hat auto)
    if (shouldAnimate) {
      scroller.style.scrollBehavior = 'smooth';
      scroller.scrollTo({
        left: targetOffsetLeft,
        behavior: 'smooth',
      });
      
      // FIX: Fallback für Browser ohne scrollend support
      const resetScrollBehavior = () => {
        scroller.style.scrollBehavior = '';
      };
      
      if ('onscrollend' in window) {
        scroller.addEventListener('scrollend', resetScrollBehavior, { once: true });
      } else {
        // Fallback: setTimeout falls scrollend nicht supported
        window.setTimeout(resetScrollBehavior, 500);
      }
    } else {
      scroller.scrollTo({
        left: targetOffsetLeft,
        behavior: 'auto',
      });
    }
  }

  /** @param {Event} [event] */
  next(event) {
    event?.preventDefault?.();
    const nextIndex = Math.min(this.#currentIndex + 1, this.snapSlides.length - 1);
    this.select(nextIndex, event);
  }

  /** @param {Event} [event] */
  previous(event) {
    event?.preventDefault?.();
    const prevIndex = Math.max(this.#currentIndex - 1, 0);
    this.select(prevIndex, event);
  }

  pause() {}
  play() {}

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get snapSlides() {
    const scroller = this.refs.snapScroller;
    if (!(scroller instanceof HTMLElement)) return [];
    return /** @type {HTMLElement[]} */ (Array.from(scroller.children).filter((el) => el instanceof HTMLElement));
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
